import React, { useEffect, useState } from 'react';
import { cn } from '../../utils/cn';
import { projectPalette, projectInitials } from './projectPalette';

/**
 * Couverture d'un projet — image si elle existe, dégradé de marque sinon.
 *
 * Le repli n'est pas un placeholder « image manquante » : c'est un visuel
 * construit (dégradé dérivé de `accentColor` ou, à défaut, du slug + texture
 * géométrique + initiales) pour qu'un projet sans photo reste présentable.
 * Une image qui échoue au chargement retombe sur ce même dégradé.
 *
 * Utilisé par la carte de carrousel, la grille de la liste et le hero du détail.
 */

const RATIOS = {
  card:   'aspect-[16/10]',
  wide:   'aspect-[21/9]',
  square: 'aspect-square',
  hero:   'aspect-[16/9] sm:aspect-[21/9] lg:aspect-[5/2]',
  fill:   'h-full',
};

const OVERLAYS = {
  none:   null,
  soft:   'bg-gradient-to-t from-ink/70 via-ink/20 to-transparent',
  strong: 'bg-gradient-to-t from-ink/90 via-ink/55 to-ink/25',
};

export function ProjectCover({
  title,
  slug,
  src,
  accentColor,
  ratio = 'card',
  overlay = 'soft',
  rounded = 'rounded-xl',
  className,
  children,
  ...rest
}) {
  const [hasImageFailed, setHasImageFailed] = useState(false);

  // Nouvelle source (navigation, filtre) : on redonne sa chance à l'image.
  useEffect(() => { setHasImageFailed(false); }, [src]);

  const palette = projectPalette({ slug, title, accentColor });
  const showImage = Boolean(src && String(src).trim()) && !hasImageFailed;
  const initials = projectInitials(title);

  return (
    <div
      className={cn('relative overflow-hidden bg-ink', RATIOS[ratio], rounded, className)}
      style={{ backgroundImage: `linear-gradient(135deg, ${palette.from} 0%, ${palette.to} 100%)` }}
      {...rest}
    >
      {showImage ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          aria-hidden
          onError={() => setHasImageFailed(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <CoverPattern initials={initials} accent={palette.accent} />
      )}

      {OVERLAYS[overlay] && (
        <div className={cn('absolute inset-0 pointer-events-none', OVERLAYS[overlay])} aria-hidden />
      )}

      {children && <div className="relative h-full">{children}</div>}
    </div>
  );
}

/**
 * Texture du repli : grille de points + deux arcs concentriques + initiales.
 * Tout est en SVG inline (aucune requête réseau, net à toutes les tailles).
 */
function CoverPattern({ initials, accent }) {
  // Suffixe stable par initiales : évite que deux <pattern> se marchent dessus
  // quand plusieurs couvertures cohabitent dans une grille.
  const patternId = `project-cover-dots-${initials.toLowerCase()}`;

  return (
    // containerType ici (et pas sur le texte) : les unités cqw des initiales
    // doivent se résoudre contre la couverture, pas contre le viewport.
    <div className="absolute inset-0" style={{ containerType: 'inline-size' }} aria-hidden>
      <svg
        className="absolute inset-0 w-full h-full"
        preserveAspectRatio="xMidYMid slice"
        viewBox="0 0 400 250"
        role="presentation"
      >
        <defs>
          <pattern id={patternId} width="16" height="16" patternUnits="userSpaceOnUse">
            <circle cx="1.5" cy="1.5" r="1.5" fill={accent} fillOpacity="0.22" />
          </pattern>
        </defs>

        <rect width="400" height="250" fill={`url(#${patternId})`} />

        <circle cx="330" cy="40" r="120" fill="none" stroke={accent} strokeOpacity="0.28" strokeWidth="1" />
        <circle cx="330" cy="40" r="180" fill="none" stroke={accent} strokeOpacity="0.16" strokeWidth="1" />
        <circle cx="40" cy="230" r="90" fill={accent} fillOpacity="0.10" />
      </svg>

      <span
        className="absolute inset-0 flex items-center justify-center font-serif italic text-cream/25 select-none
                   text-[clamp(3rem,18cqw,9rem)] leading-none"
      >
        {initials}
      </span>
    </div>
  );
}

export default ProjectCover;
